import { createAsyncThunk } from '@reduxjs/toolkit'
import { ServiceConfig } from '../services/config'
import { APIError, get, post } from '../services/crud'
import { getAllKnockStatuses } from '../knockStatus/api'
import {
  selectAllKnockStatuses,
  selectKnockStatusesLoaded,
} from '../knockStatus/slice'
import {
  selectKnockStatusFilterEnabled,
  selectTenantFeaturesLoaded,
} from '../tenantFeatures/selectors'
import { StoreData } from '../reducers/typings'
import {
  applyKnockStatusRulesToPref,
  prefsHaveKnockStatusFilter,
} from './knockStatusFilterPrefs'
import { IUserPreferenceFilter, IUserPreferenceListView } from './typings'

const baseUrl = `${ServiceConfig.baseAPIUrl}/UserPreferences`

export const loadFilterPreferences = createAsyncThunk<
  IUserPreferenceFilter[],
  void,
  { state: StoreData; rejectValue: APIError }
>('userPreferences/filters/load', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/filter`)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())

  const prefs: IUserPreferenceFilter[] = await response.json()

  // A saved knock-status selection outlives both the tenant feature that
  // exposes the filter and the knock status it points at, so reconcile it here
  // rather than in the slices: every filter set is hydrated from this payload,
  // and this runs before any page builds a query from it (WWM-17972).
  if (!prefsHaveKnockStatusFilter(prefs)) return prefs

  const state = thunkApi.getState()

  // Features arrive with the login / refresh-token payload. If they somehow
  // haven't yet, leave the selection alone instead of dropping it.
  if (!selectTenantFeaturesLoaded(state)) return prefs

  if (!selectKnockStatusFilterEnabled(state))
    return prefs.map((pref) => applyKnockStatusRulesToPref(pref, null))

  if (!selectKnockStatusesLoaded(state))
    await thunkApi.dispatch(getAllKnockStatuses())

  const knockStatuses = selectAllKnockStatuses(thunkApi.getState())
  return prefs.map((pref) => applyKnockStatusRulesToPref(pref, knockStatuses))
})

export const saveFilterPreference = createAsyncThunk<
  IUserPreferenceFilter,
  IUserPreferenceFilter,
  { rejectValue: APIError }
>('userPreferences/filters/save', async (pref, thunkApi) => {
  const response = await post(`${baseUrl}/filter`, pref)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const loadListViewPreferences = createAsyncThunk<
  IUserPreferenceListView[],
  void,
  { rejectValue: APIError }
>('userPreferences/listView/load', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/listView`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const saveListViewPreference = createAsyncThunk<
  IUserPreferenceListView,
  IUserPreferenceListView,
  { rejectValue: APIError }
>('userPreferences/listView/save', async (pref, thunkApi) => {
  const response = await post(`${baseUrl}/listView`, pref)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})
